import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardHeader,
  CardContent,
  Dialog,
  DialogActions,
  DialogTitle,
  Grid,
  Avatar,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
  Switch,
  TextField,
  Checkbox,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { capitalize } from "../../utils/helpers";
import { toast } from "react-toastify";
import {
  EditOutlined,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import { fDate } from "../../utils/formatTime";
import CommonDataTable from "../../components/CommonDataTable";
import Swal from "sweetalert2";
import errorHandler from "../../utils/errorHandler";
import TableFilter from "../../components/shared/TableFilter";
import usePageTitle from "../../hooks/usePageTitle";
import {
  IMAGE_BASE_URL,
  STATUS_OPTIONS_LIST,
  LIST_PAGE_TOOLBAR_SX,
  LIST_PAGE_CARD_CONTENT_SX,
  LIST_PAGE_ADD_BUTTON_SX,
} from "../../utils/constants";
import { GlobalServices } from "../../services/global";

export default function CategoryView() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [searchItem, setSearchItem] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [data, setData] = useState([]);
  const [warning, setWarning] = useState("");
  const [action, setAction] = useState("");
  const [show, setShow] = useState(false);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [statusId, setStatusId] = useState("");
  const [refresh, setRefresh] = useState(false);
  const [status, setStatus] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [loaderDelay, setLoaderDelay] = useState(false);
  const [dataCategory, setDataCategory] = useState({});

  const [globalsData, setGlobalsData] = useState({});

  usePageTitle(capitalize(dataCategory?.title || "Parent Category"), {
    back: true,
  });

  useEffect(() => {
    // Initial fetch to populate header, though we do it again in the main useEffect
  }, [id]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDebouncedSearch(searchItem);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchItem]);

  useEffect(() => {
    setIsLoading(true);
    setLoaderDelay(false);

    GlobalServices.getAllGlobalSettings()
      .then((res) => {
        if (res?.data?.status) {
          const globals = res.data.data;
          setGlobalsData(globals);

          const businessTypes = globals.vendorBusinessTypes || [];
          const currentCategoryData = businessTypes[id];

          if (currentCategoryData) {
            const catObj = currentCategoryData.category || {};
            setDataCategory({
              name: typeof catObj === "string" ? catObj : catObj.title || "",
              title: typeof catObj === "string" ? catObj : catObj.title || "",
              description:
                typeof catObj === "string" ? "" : catObj.description || "",
              image: typeof catObj === "string" ? "" : catObj.image || "",
              status: (typeof catObj === "object" ? catObj.status : "") || "active",
              createdAt: new Date().toISOString(),
            });

            let subs = (currentCategoryData.subcategories || []).map(
              (sub, idx) => {
                const subTitle = typeof sub === "object" ? (sub.title || sub.name || "") : sub;
                const subStatus = typeof sub === "object" ? (sub.status || "active") : "active";
                return {
                  _id: idx.toString(),
                  name: subTitle,
                  status: subStatus,
                  createdAt: new Date().toISOString(),
                };
              },
            );

            if (debouncedSearch) {
              subs = subs.filter((s) =>
                s.name.toLowerCase().includes(debouncedSearch.toLowerCase()),
              );
            }

            setTotalPages(subs.length);
            const startIndex = (currentPage - 1) * pageSize;
            const paginated = subs.slice(startIndex, startIndex + pageSize);

            setTimeout(() => {
              setData(paginated);
              setLoaderDelay(true);
            }, 500);
          } else {
            setTimeout(() => {
              setData([]);
              setTotalPages(0);
              setLoaderDelay(true);
            }, 500);
          }
        }
      })
      .catch((err) => {
        errorHandler(err);
        setLoaderDelay(false);
      })
      .finally(() => {
        setIsLoading(false);
        setLoaderDelay(false);
      });
  }, [debouncedSearch, currentPage, pageSize, refresh, status, id]);

  const handlePageChange = (page) => setCurrentPage(page);

  const handleClose = () => setShow(false);

  const deleteData = async (ids) => {
    const isMultiple = Array.isArray(ids);
    Swal.fire({
      text: isMultiple
        ? `Are you sure you want to delete ${ids.length} selected subcategory(ies)?`
        : "Are you sure you want to delete this Sub Category?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const subCategoryIds = isMultiple ? ids.map(Number) : [Number(ids)];

          if (!globalsData || !globalsData.vendorBusinessTypes) {
            toast.error("Global data not loaded.");
            return;
          }

          let businessTypes = [...globalsData.vendorBusinessTypes];
          const currentCategoryData = businessTypes[id];
          if (!currentCategoryData) return;

          currentCategoryData.subcategories = (
            currentCategoryData.subcategories || []
          ).filter((_, idx) => !subCategoryIds.includes(idx));

          // Format for API: subcategories as array of objects { title, status, description }
          const formattedBusinessTypes = businessTypes.map((item) => {
            const subs = Array.isArray(item.subcategories)
              ? item.subcategories.map((s) => {
                if (typeof s === "object" && s) {
                  return { title: s.title || s.name || "", status: s.status || "active", description: s.description || "" };
                }
                return { title: String(s || ""), status: "active", description: "" };
              })
              : [];

            return {
              category: typeof item.category === "object" ? item.category : {
                title: typeof item.category === "string" ? item.category : "Unnamed",
                description: "",
                image: "",
                status: "active",
              },
              subcategories: subs,
            };
          });

          const payload = {
            ...globalsData,
            vendorBusinessTypes: formattedBusinessTypes,
          };

          delete payload._id;
          delete payload.createdAt;
          delete payload.updatedAt;
          delete payload.__v;

          const res = await GlobalServices.updateGlobalSettings(payload);

          if (res?.data?.status) {
            toast.success("Subcategory deleted successfully", {
              toastId: "success",
            });
            if (!isMultiple) {
              setData((prevData) =>
                prevData.filter((item) => item._id !== ids),
              );
              setTotalPages((prevTotal) => prevTotal - 1);
            } else {
              setData((prevData) =>
                prevData.filter((item) => !ids.includes(item._id)),
              );
              setTotalPages((prevTotal) => Math.max(0, prevTotal - ids.length));
            }
            setRefresh((v) => !v);
          } else {
            toast.error(res?.data?.message || "Failed to delete subcategory", {
              toastId: "error",
            });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const toggleChecked = async (toggleId, currentSt) => {
    try {
      const newStatus = (currentSt === "active" || currentSt === 1) ? "inactive" : "active";

      if (!globalsData || !globalsData.vendorBusinessTypes) {
        toast.error("Global data not loaded.");
        setShow(false);
        return;
      }

      // Clone businessTypes and update status on the target subcategory
      const businessTypes = [...globalsData.vendorBusinessTypes];
      const currentCategoryData = businessTypes[id];
      if (!currentCategoryData) {
        toast.error("Category not found.");
        setShow(false);
        return;
      }

      const subIndex = Number(toggleId);
      const subs = [...(currentCategoryData.subcategories || [])];

      // Update the subcategory status — convert to object { title, status, description } if needed
      if (subs[subIndex] !== undefined) {
        const subValue = subs[subIndex];
        if (typeof subValue === "object") {
          subs[subIndex] = { ...subValue, status: newStatus };
        } else {
          subs[subIndex] = { title: subValue, status: newStatus, description: "" };
        }
      }

      currentCategoryData.subcategories = subs;

      // Format for API: subcategories as array of objects { title, status, description }
      const formattedBusinessTypes = businessTypes.map((item) => {
        const formattedSubs = Array.isArray(item.subcategories)
          ? item.subcategories.map((s) => {
            if (typeof s === "object" && s) {
              return { title: s.title || s.name || "", status: s.status || "active", description: s.description || "" };
            }
            return { title: String(s || ""), status: "active", description: "" };
          })
          : [];

        return {
          category: typeof item.category === "object" ? item.category : {
            title: typeof item.category === "string" ? item.category : "Unnamed",
            description: "",
            image: "",
            status: "active",
          },
          subcategories: formattedSubs,
        };
      });

      const payload = {
        ...globalsData,
        vendorBusinessTypes: formattedBusinessTypes,
      };

      delete payload._id;
      delete payload.createdAt;
      delete payload.updatedAt;
      delete payload.__v;

      const res = await GlobalServices.updateGlobalSettings(payload);

      if (res?.data?.status) {
        // Update local state to reflect the toggle
        setData((prevData) =>
          prevData.map((item) =>
            item._id === toggleId ? { ...item, status: newStatus } : item,
          ),
        );
        toast.success(
          `Subcategory ${newStatus === "active" ? "activated" : "deactivated"} successfully`,
          { toastId: "success1" },
        );
      } else {
        toast.error(res?.data?.message || "Failed to update status", {
          toastId: "error",
        });
      }
    } catch (error) {
      errorHandler(error);
    } finally {
      setShow(false);
    }
  };

  const columns = [
    {
      name: "S.No.",
      cell: (row, index) => (currentPage - 1) * pageSize + index + 1,
    },
    {
      name: "Name",
      sortable: true,
      selector: (row) => capitalize(row.name),
    },
    {
      name: "Created At",
      sortable: true,
      selector: (row) => fDate(row.createdAt),
    },
    {
      name: "Status",
      cell: (row) => (
        <Box display="flex" alignItems="center" gap={1}>
          <Switch
            checked={row.status === "active" || row.status === 1}
            onClick={() => {
              setShow(true);
              setCurrentStatus(row.status);
              setStatusId(row._id);
              setAction("status");
              setWarning(
                `Are you sure you want to ${row.status === "active" || row.status === 1
                  ? "deactivate"
                  : "activate"
                } this subcategory?`,
              );
            }}
            inputProps={{ "aria-label": "controlled" }}
            color="primary"
          />
        </Box>
      ),
    },
    {
      name: "Actions",
      center: true,
      cell: (row) => (
        <Box display="flex" gap={1} justifyContent="center">
          {/* <Tooltip title="View Details">
            <IconButton
              color="primary"
              size="small"
              onClick={() => navigate(`/category/viewsubcategory/${row?._id}`)}
            >
              <VisibilityIcon />
            </IconButton>
          </Tooltip> */}
          <Tooltip title="Edit">
            <IconButton
              color="primary"
              size="small"
              onClick={() =>
                navigate(`/category/edit-subcategory/${id}/${row?._id}`)
              }
            >
              <EditOutlined />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              color="error"
              size="small"
              onClick={() => deleteData(row?._id)}
            >
              <DeleteIcon />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box>
      <Card
        sx={{
          background: "linear-gradient(135deg, #394657, #e96c0f)",
          color: "white",
        }}
      >
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            {/* <Grid item>
              <Avatar
                src={
                  dataCategory?.image ? IMAGE_BASE_URL + dataCategory.image : ""
                }
                alt={dataCategory?.name}
                sx={{
                  width: 120,
                  height: 120,
                  border: "4px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                }}
              />
            </Grid> */}
            <Grid item xs={12} sm={9} md={10}>
              <Box>
                <Typography variant="h3" fontWeight="bold" gutterBottom>
                  {capitalize(dataCategory?.title || "Parent Category")}
                </Typography>
                {/* <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                  {capitalize(
                    dataCategory?.description || "No description available",
                  )}
                </Typography> */}
                <Grid container spacing={4} sx={{ mt: 2 }}>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {data?.length || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Subcategories
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {dataCategory?.status === "active"
                          ? "Active"
                          : "Inactive"}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Status
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Box textAlign="center">
                      <Typography variant="h4" fontWeight="bold">
                        {fDate(dataCategory?.createdAt)}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Created Date
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Subcategories Section */}
      <Card>
        {/* <CardLayout> */}

        <Box className="list-page-toolbar" sx={LIST_PAGE_TOOLBAR_SX}>
          <TableFilter
            data={STATUS_OPTIONS_LIST}
            value={status}
            setValue={setStatus}
            filterName="Status"
          />
          <TextField
            label="Search"
            variant="standard"
            value={searchItem}
            onChange={(e) => setSearchItem(e.target.value)}
            InputProps={{
              endAdornment: searchItem ? (
                <InputAdornment position="end">
                  <IconButton
                    aria-label="clear-search"
                    size="small"
                    onClick={() => setSearchItem("")}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate(`/category/add-subcategory/${id}`)}
            sx={LIST_PAGE_ADD_BUTTON_SX}
          >
            Add Subcategory
          </Button>
        </Box>
        <CardContent sx={LIST_PAGE_CARD_CONTENT_SX}>
          <CommonDataTable
            columns={columns}
            data={data}
            isLoading={isLoading || !loaderDelay}
            totalCount={totalPages}
            currentPage={currentPage}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onRowsPerPageChange={(currentRowsPerPage, newCurrentPage) => {
              setPageSize(currentRowsPerPage);
              setCurrentPage(newCurrentPage);
            }}
          />
        </CardContent>
        {/* </CardLayout> */}
      </Card>
      <Dialog
        open={show}
        keepMounted
        onClose={handleClose}
        aria-describedby="alert-dialog-slide-description"
        className="popupbox-statis"
      >
        <DialogTitle as="h3">{warning}</DialogTitle>
        <DialogActions className="status-box-action">
          <Button
            variant="outlined"
            onClick={() => {
              if (action === "status") {
                toggleChecked(statusId, currentStatus);
              }
            }}
          >
            Yes
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            No
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
